// UTXO Transaction Signing via WalletConnect Universal Provider
// Based on Bifrost Wallet's implementation: sendTransfer method for UTXO chains
// Supports: BTC, LTC, DOGE, BCH, RVN, ZEC, DASH, KMD, QTUM
import { UniversalProvider } from '@walletconnect/universal-provider';
import { WalletConnectionError } from './walletConnectors';
import { toast } from 'sonner';

const PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '00bac352e71e1e141413ca1edd9335c6';

let universalProvider: UniversalProvider | null = null;

// Bitcoin genesis block hash (used for bip122 chain ID)
const BITCOIN_GENESIS = '000000000019d6689c085ae165831e934ff763ae46a2a6c172b3f1b60a8ce26f';

/**
 * Map UTXO token to chain identifier for sendTransfer method
 * Based on Bifrost Wallet's implementation
 */
function getUTXOChainIdentifier(tokenSymbol: string): string {
  const upperSymbol = tokenSymbol.toUpperCase();
  const chainMap: Record<string, string> = {
    'BTC': 'bitcoin',
    'LTC': 'litecoin',
    'DOGE': 'dogecoin',
    'BCH': 'bitcoincash',
    'RVN': 'ravencoin',
    'ZEC': 'zcash',
    'DASH': 'dash',
    'KMD': 'komodo',
    'QTUM': 'qtum',
  };
  return chainMap[upperSymbol] || 'bitcoin';
}

/**
 * Convert amount to satoshis (8 decimals for most UTXO)
 */
function toSatoshis(amount: number): string {
  return Math.floor(amount * 100000000).toString();
}

/**
 * Initialize Universal Provider with Bitcoin namespace
 */
export async function initializeUTXOProvider(): Promise<UniversalProvider> {
  if (universalProvider && universalProvider.session) {
    return universalProvider;
  }

  try {
    universalProvider = await UniversalProvider.init({
      projectId: PROJECT_ID,
      metadata: {
        name: 'KawSwap DEX',
        description: 'Decentralized Exchange on Internet Computer',
        url: window.location.origin,
        icons: [
          `${window.location.origin}/vite.svg`,
          `${window.location.origin}/favicon.ico`,
          'https://kawswap.io/favicon.ico',
        ],
      },
    });

    return universalProvider;
  } catch (error: any) {
    throw new WalletConnectionError(
      `Failed to initialize UTXO Provider: ${error?.message || 'Unknown error'}`,
      'INIT_ERROR'
    );
  }
}

/**
 * Connect Universal Provider with Bitcoin namespace (supports all UTXO chains via sendTransfer)
 * Based on Bifrost Wallet's approach
 */
export async function connectUTXOProvider(): Promise<void> {
  const provider = await initializeUTXOProvider();

  // If already connected, check if session exists and return early
  if (provider.session) {
    console.log('[UTXO Provider] Already connected. Session exists.');
    logWalletCapabilities();
    return;
  }

  try {
    console.log('[UTXO Provider] Attempting to connect with bip122 namespace...');
    
    // Add timeout to prevent hanging if wallet doesn't support bip122
    const connectPromise = provider.connect({
      namespaces: {
        // EVM chains (keep existing support)
        eip155: {
          methods: ['eth_sendTransaction', 'eth_signTransaction', 'eth_sign', 'personal_sign'],
          chains: ['eip155:1'], // Ethereum Mainnet
          events: ['chainChanged', 'accountsChanged'],
        },
        // Bitcoin namespace - Bifrost uses this for all UTXO chains
        // The sendTransfer method accepts a 'chain' parameter to specify which UTXO chain
        bip122: {
          methods: [
            'sendTransfer', // Bifrost's method - works for BTC, LTC, DOGE, RVN, etc.
            'btc_signTransaction', // Standard Bitcoin method (fallback)
            'utxo_signTransaction', // Generic UTXO method (fallback)
            'getAccountAddresses', // Get wallet addresses
            'getBalance', // Get balance for UTXO chains
            'btc_getBalance', // Bitcoin-specific balance method
            'utxo_getBalance', // Generic UTXO balance method
            'getAccountBalance', // Alternative balance method
            'bip122_addressesChanged', // Event for address changes
          ],
          chains: [`bip122:${BITCOIN_GENESIS}`], // Bitcoin mainnet chain ID
          events: ['bip122_addressesChanged'],
        },
      },
    });
    
    // Add 10 second timeout - if wallet doesn't support bip122, it may hang
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout: Wallet may not support bip122 namespace')), 10000);
    });
    
    await Promise.race([connectPromise, timeoutPromise]);
    
    // Log capabilities after connection to see what wallet actually supports
    console.log('[UTXO Provider] Connection successful. Checking wallet capabilities...');
    logWalletCapabilities();
    
    // Auto-fetch UTXO addresses if bip122 is supported
    if (provider.session && provider.session.namespaces.bip122) {
      try {
        const addresses = await getUTXOAddresses();
        if (addresses.length > 0) {
          console.log('[UTXO Provider] Auto-fetched UTXO addresses:', addresses);
        }
      } catch (error) {
        console.warn('[UTXO Provider] Could not auto-fetch addresses (non-critical):', error);
      }
    }
  } catch (error: any) {
    console.error('[UTXO Provider] Connection failed:', error);
    if (error.code === 4001) {
      throw new WalletConnectionError('User rejected the connection request.', 'USER_REJECTED');
    }
    if (error.message?.includes('timeout')) {
      console.warn('[UTXO Provider] Connection timeout - wallet likely does not support bip122 namespace');
      // Don't throw - allow the code to continue and check if session exists anyway
      // Some wallets may have connected but not responded in time
      if (provider.session) {
        console.log('[UTXO Provider] Session exists despite timeout - continuing...');
        logWalletCapabilities();
        return;
      }
      throw new WalletConnectionError(
        'Connection timeout: The connected wallet does not support UTXO transaction signing (bip122 namespace).',
        'TIMEOUT'
      );
    }
    throw new WalletConnectionError(
      error?.message || 'Failed to connect UTXO Provider',
      'CONNECTION_FAILED'
    );
  }
}

export function getUTXOProvider(): UniversalProvider | null {
  return universalProvider;
}

/**
 * Sign UTXO transaction for ANY supported UTXO chain (BTC, LTC, DOGE, RVN, etc.)
 * Tries multiple methods: sendTransfer (Bifrost), btc_signTransaction (standard), utxo_signTransaction (generic)
 */
export async function signUTXOTransaction(
  tokenSymbol: string,
  from: string,
  to: string,
  amount: number
): Promise<{ rawTx: string; txHash: string }> {
  const provider = getUTXOProvider();
  if (!provider || !provider.session) {
    throw new WalletConnectionError(
      'UTXO Provider not connected. Please connect a wallet that supports UTXO signing.',
      'NO_PROVIDER'
    );
  }

  // Log wallet capabilities for debugging
  logWalletCapabilities();

  try {
    // Check if bip122 namespace is available
    const namespaces = provider.session.namespaces;
    if (!namespaces.bip122) {
      const capabilities = getWalletCapabilities();
      console.error('[UTXO Signing] Wallet does not support bip122 namespace. Available namespaces:', capabilities.allNamespaces);
      throw new WalletConnectionError(
        'Connected wallet does not support UTXO transaction signing (missing bip122 namespace).',
        'NAMESPACE_NOT_SUPPORTED'
      );
    }

    const chainIdentifier = getUTXOChainIdentifier(tokenSymbol);
    const amountSatoshis = toSatoshis(amount);
    const bip122Methods = namespaces.bip122.methods || [];
    
    console.log('[UTXO Signing] Available bip122 methods:', bip122Methods);
    console.log('[UTXO Signing] Attempting to sign', tokenSymbol, 'transaction');

    // Try multiple methods in order of preference
    const methodsToTry = [
      {
        name: 'sendTransfer',
        params: {
          chain: chainIdentifier,
          to: to,
          amount: amountSatoshis,
        },
        description: 'Bifrost Wallet method',
      },
      {
        name: 'btc_signTransaction',
        params: {
          from: from,
          to: to,
          amount: amountSatoshis,
          chain: chainIdentifier,
        },
        description: 'Standard Bitcoin method',
      },
      {
        name: 'utxo_signTransaction',
        params: {
          from: from,
          to: to,
          amount: amountSatoshis,
          chain: chainIdentifier,
        },
        description: 'Generic UTXO method',
      },
    ];

    // Filter to only try methods that are actually available
    const availableMethods = methodsToTry.filter(m => bip122Methods.includes(m.name));
    
    if (availableMethods.length === 0) {
      console.error('[UTXO Signing] No supported methods found. Available methods:', bip122Methods);
      throw new WalletConnectionError(
        `Wallet does not support any known UTXO signing methods. Available methods: ${bip122Methods.join(', ')}`,
        'METHOD_NOT_SUPPORTED'
      );
    }

    console.log('[UTXO Signing] Will try methods:', availableMethods.map(m => m.name).join(', '));

    // Try each method until one works
    let lastError: any = null;
    for (const method of availableMethods) {
      try {
        console.log(`[UTXO Signing] Trying method: ${method.name} (${method.description})`);
        
        const response = await provider.request({
          topic: provider.session.topic,
          chainId: `bip122:${BITCOIN_GENESIS}`,
          request: {
            method: method.name,
            params: method.params,
          },
        });

        console.log(`[UTXO Signing] Success with method: ${method.name}`, response);

        // Response format may vary by wallet
        return {
          rawTx: response.rawTx || response.hex || response.transaction || '',
          txHash: response.txHash || response.txid || response.hash || response.id || '',
        };
      } catch (methodError: any) {
        console.warn(`[UTXO Signing] Method ${method.name} failed:`, methodError);
        lastError = methodError;
        // Continue to next method
      }
    }

    // All methods failed
    if (lastError?.code === 4001) {
      throw new WalletConnectionError('User rejected the transaction.', 'USER_REJECTED');
    }
    throw new WalletConnectionError(
      lastError?.message || `Failed to sign ${tokenSymbol} transaction. Tried methods: ${availableMethods.map(m => m.name).join(', ')}`,
      lastError?.code || 'SIGNING_ERROR'
    );
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    if (error.code === 4001) {
      throw new WalletConnectionError('User rejected the transaction.', 'USER_REJECTED');
    }
    throw new WalletConnectionError(
      error.message || `Failed to sign ${tokenSymbol} transaction`,
      error.code || 'SIGNING_ERROR'
    );
  }
}

/**
 * Get detailed wallet capabilities - what namespaces and methods are supported
 */
export function getWalletCapabilities(): {
  hasBip122: boolean;
  hasEip155: boolean;
  bip122Methods: string[];
  eip155Methods: string[];
  allNamespaces: string[];
  sessionInfo: any;
} {
  const provider = getUTXOProvider();
  if (!provider || !provider.session) {
    return {
      hasBip122: false,
      hasEip155: false,
      bip122Methods: [],
      eip155Methods: [],
      allNamespaces: [],
      sessionInfo: null,
    };
  }

  const namespaces = provider.session.namespaces;
  const allNamespaces = Object.keys(namespaces);
  
  const bip122Namespace = namespaces.bip122;
  const eip155Namespace = namespaces.eip155;

  return {
    hasBip122: !!bip122Namespace,
    hasEip155: !!eip155Namespace,
    bip122Methods: bip122Namespace?.methods || [],
    eip155Methods: eip155Namespace?.methods || [],
    allNamespaces,
    sessionInfo: {
      topic: provider.session.topic,
      peer: provider.session.peer,
      namespaces: namespaces,
    },
  };
}

/**
 * Log wallet capabilities to console for debugging
 */
export function logWalletCapabilities(): void {
  const capabilities = getWalletCapabilities();
  
  console.log('=== Wallet Capabilities Debug ===');
  console.log('Has bip122 namespace:', capabilities.hasBip122);
  console.log('Has eip155 namespace:', capabilities.hasEip155);
  console.log('All namespaces:', capabilities.allNamespaces);
  console.log('bip122 methods:', capabilities.bip122Methods);
  console.log('eip155 methods:', capabilities.eip155Methods);
  console.log('Full session info:', capabilities.sessionInfo);
  console.log('================================');
  
  // Also log to toast for user visibility
  if (capabilities.hasBip122) {
    toast.info('Wallet UTXO Support Detected', {
      description: `bip122 namespace found. Methods: ${capabilities.bip122Methods.join(', ')}`,
      duration: 5000,
    });
  } else {
    toast.warning('No UTXO Support', {
      description: 'Connected wallet does not support bip122 namespace for UTXO signing.',
      duration: 5000,
    });
  }
}

/**
 * Check if wallet supports UTXO signing
 */
export function checkUTXOSupport(): boolean {
  const provider = getUTXOProvider();
  if (!provider || !provider.session) return false;
  
  const namespaces = provider.session.namespaces;
  return !!namespaces.bip122;
}

/**
 * Get wallet addresses for UTXO chains
 */
export async function getUTXOAddresses(): Promise<string[]> {
  const provider = getUTXOProvider();
  if (!provider || !provider.session) {
    throw new WalletConnectionError('UTXO Provider not connected', 'NO_PROVIDER');
  }

  try {
    const response = await provider.request({
      topic: provider.session.topic,
      chainId: `bip122:${BITCOIN_GENESIS}`,
      request: {
        method: 'getAccountAddresses',
        params: {},
      },
    });

    return response.addresses || response || [];
  } catch (error: any) {
    throw new WalletConnectionError(
      error.message || 'Failed to get UTXO addresses',
      error.code || 'REQUEST_ERROR'
    );
  }
}

/**
 * Get UTXO balance for a specific chain via WalletConnect
 * Works with wallets that support bip122 namespace
 * Falls back to RPC if wallet method is not available
 */
export async function getUTXOBalance(
  tokenSymbol: string,
  address?: string
): Promise<number> {
  const provider = getUTXOProvider();
  if (!provider || !provider.session) {
    // If no provider, fall back to RPC immediately
    return await getUTXOBalanceViaRPC(tokenSymbol, address);
  }

  try {
    const namespaces = provider.session.namespaces;
    if (!namespaces.bip122) {
      // No bip122 support, use RPC fallback
      return await getUTXOBalanceViaRPC(tokenSymbol, address);
    }

    const chainIdentifier = getUTXOChainIdentifier(tokenSymbol);
    const bip122Methods = namespaces.bip122.methods || [];

    // Try multiple balance methods (wallet-specific)
    const balanceMethods = [
      { name: 'getBalance', params: { chain: chainIdentifier, address } },
      { name: 'btc_getBalance', params: { chain: chainIdentifier, address } },
      { name: 'utxo_getBalance', params: { chain: chainIdentifier, address } },
      { name: 'getAccountBalance', params: { chain: chainIdentifier } },
    ];

    const availableMethod = balanceMethods.find(m => bip122Methods.includes(m.name));
    
    if (availableMethod) {
      try {
        const response = await provider.request({
          topic: provider.session.topic,
          chainId: `bip122:${BITCOIN_GENESIS}`,
          request: {
            method: availableMethod.name,
            params: availableMethod.params,
          },
        });

        // Parse response (format may vary by wallet)
        const balance = response.balance || response.amount || response.value || 0;
        const balanceNum = typeof balance === 'string' ? parseFloat(balance) : balance;
        
        // Convert from satoshis to main unit if needed (most wallets return in satoshis)
        return balanceNum > 1000000 ? balanceNum / 100000000 : balanceNum;
      } catch (error) {
        console.warn(`[UTXO Balance] Wallet method ${availableMethod.name} failed, using RPC fallback:`, error);
        // Fall through to RPC fallback
      }
    }

    // Fallback: use address from wallet and fetch via RPC
    if (!address) {
      try {
        const addresses = await getUTXOAddresses();
        if (addresses.length > 0) {
          address = addresses[0];
        }
      } catch (error) {
        console.warn('[UTXO Balance] Could not get addresses from wallet:', error);
      }
    }

    return await getUTXOBalanceViaRPC(tokenSymbol, address);
  } catch (error: any) {
    // Always fall back to RPC if wallet method fails
    return await getUTXOBalanceViaRPC(tokenSymbol, address);
  }
}

/**
 * Fallback: Get UTXO balance via RPC (when WalletConnect method is not available)
 */
async function getUTXOBalanceViaRPC(
  tokenSymbol: string,
  address?: string
): Promise<number> {
  if (!address) {
    return 0;
  }

  const upperSymbol = tokenSymbol.toUpperCase();
  
  try {
    // Use existing RPC balance fetchers
    if (upperSymbol === 'RVN') {
      const { fetchRavencoinBalance } = await import('./walletConnectors');
      return await fetchRavencoinBalance(address);
    }
    
    // For other UTXO chains, try to use multiChainRPC if available
    // This provides RPC fallback for BTC, LTC, DOGE, BCH, etc.
    try {
      const { fetchNativeBalance } = await import('./multiChainRPC');
      const chainMap: Record<string, string> = {
        'BTC': 'bitcoin',
        'LTC': 'litecoin',
        'DOGE': 'dogecoin',
        'BCH': 'bitcoinCash',
        'ZEC': 'zcash',
        'DASH': 'dash',
        'KMD': 'komodo',
        'QTUM': 'qtum',
      };
      
      const chainId = chainMap[upperSymbol];
      if (chainId) {
        const balance = await fetchNativeBalance(chainId, address);
        if (import.meta.env.DEV) {
          console.log(`✅ [UTXO Balance] ${upperSymbol} (via RPC):`, balance);
        }
        return balance;
      }
    } catch (rpcError) {
      if (import.meta.env.DEV) {
        console.warn(`⚠️ [UTXO Balance] RPC fetch failed for ${upperSymbol}:`, rpcError);
      }
    }
    
    // If RPC fetch failed or chain not mapped, return 0
    if (import.meta.env.DEV) {
      console.log(`[UTXO Balance] RPC fetching for ${upperSymbol} not available - wallet method should be used instead`);
    }
    return 0;
  } catch (error) {
    console.warn(`[UTXO Balance] RPC fetch failed for ${upperSymbol}:`, error);
    return 0;
  }
}
